// Turns runs of adjacent images in page content into one horizontal, swipeable row.
// Migrated pages carry bare <img> tags in a row — three shots per person on the team
// page — which otherwise stack full width and push the next bio far down the page.
//
// The markup that arrives is inconsistent: some runs are bare siblings, some sit inside
// a <p>, and some are split across two <p> elements. Paragraphs holding nothing but
// images are unwrapped first so all three shapes collapse to the same thing.
(() => {
  const MIN_IMAGES = 2;

  const isImage = (node) => node.nodeType === Node.ELEMENT_NODE && node.tagName === 'IMG';
  const isIgnorable = (node) => node.nodeType === Node.TEXT_NODE && !node.textContent.trim();

  const holdsOnlyImages = (el) => {
    const children = [...el.childNodes].filter((n) => !isIgnorable(n));
    return children.length > 0 && children.every(isImage);
  };

  const unwrapImageParagraphs = (container) => {
    container.querySelectorAll('p').forEach((p) => {
      if (holdsOnlyImages(p)) p.replaceWith(...p.querySelectorAll('img'));
    });
  };

  const runsWithin = (parent) => {
    const runs = [];
    let run = [];
    [...parent.childNodes].forEach((node) => {
      if (isIgnorable(node)) return;
      if (isImage(node)) {
        run.push(node);
        return;
      }
      // Anything else ends the run — a bio between two shots means they are not one set.
      if (run.length >= MIN_IMAGES) runs.push(run);
      run = [];
    });
    if (run.length >= MIN_IMAGES) runs.push(run);
    return runs;
  };

  const arrow = (direction) => {
    const btn = document.createElement('button');
    btn.type = 'button';
    btn.className = `content-image-slider__arrow content-image-slider__arrow--${direction}`;
    btn.setAttribute('aria-label', direction === 'prev' ? 'Previous image' : 'Next image');
    btn.innerHTML =
      '<svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" ' +
      'stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true" ' +
      'focusable="false"><path d="' +
      (direction === 'prev' ? 'm15 18-6-6 6-6' : 'm9 18 6-6-6-6') +
      '"/></svg>';
    return btn;
  };

  const wrapRun = (run) => {
    const frame = document.createElement('div');
    frame.className = 'content-image-slider';
    const track = document.createElement('div');
    track.className = 'content-image-slider__track';
    frame.appendChild(track);

    run[0].parentNode.insertBefore(frame, run[0]);
    run.forEach((img) => {
      const cell = document.createElement('div');
      cell.className = 'content-image-slider__item';
      cell.appendChild(img);
      track.appendChild(cell);
    });

    const prev = arrow('prev');
    const next = arrow('next');
    frame.append(prev, next);

    // Scroll to an item's own offset rather than by a distance: with mandatory snapping
    // a smooth scroll that lands between snap points gets pulled back where it started.
    const goTo = (dir) => {
      const cells = [...track.children];
      const current = track.scrollLeft;
      const target =
        dir > 0
          ? cells.find((c) => c.offsetLeft > current + 1)
          : [...cells].reverse().find((c) => c.offsetLeft < current - 1);
      track.scrollTo({ left: target ? target.offsetLeft : dir > 0 ? track.scrollWidth : 0, behavior: 'smooth' });
    };

    prev.addEventListener('click', () => goTo(-1));
    next.addEventListener('click', () => goTo(1));

    // Arrows are pointless when everything already fits, and each one is pointless at
    // its own end of the track.
    const syncArrows = () => {
      const overflows = track.scrollWidth > track.clientWidth + 1;
      frame.classList.toggle('has-overflow', overflows);
      prev.disabled = !overflows || track.scrollLeft <= 1;
      next.disabled = !overflows || track.scrollLeft + track.clientWidth >= track.scrollWidth - 1;
    };

    track.addEventListener('scroll', syncArrows, { passive: true });
    window.addEventListener('resize', syncArrows);
    // The first measurement happens while the images still have no height, so the track
    // does not overflow yet and both arrows would stay disabled. Re-measuring once each
    // image settles and again on full load covers cached and freshly fetched alike.
    run.forEach((img) => {
      if (img.complete) return;
      img.addEventListener('load', syncArrows, { once: true });
      img.addEventListener('error', syncArrows, { once: true });
    });
    window.addEventListener('load', syncArrows, { once: true });
    requestAnimationFrame(syncArrows);
    syncArrows();
  };

  const init = () => {
    document.querySelectorAll('.page-content--image-sliders').forEach((container) => {
      unwrapImageParagraphs(container);
      // Images can sit at any depth — the migrated pages wrap everything in a div —
      // so every element holding an image is a candidate parent for a run.
      const parents = new Set([...container.querySelectorAll('img')].map((img) => img.parentNode));
      parents.forEach((parent) => runsWithin(parent).forEach(wrapRun));
    });
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
